import { useFormikContext } from "formik";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCatalog } from "../context/Catalog";
import { useTramite } from "../context/Tramite";
import "./css/components.css";
import { buscarUnidad, listarCercosPerimetrales } from "../api/General";
import { isInvalid } from "../utils/forms";
import { isEditable } from "../utils/tramite";
import Loader from "./controls/Loader";
import QuestionField from "./controls/QuestionField";
import ArrayField from "./controls/ArrayField";
import Grid from "./layout/Grid";
import Row from "./layout/Row";
import Col from "./layout/Col";
import { AreaAcopioSolidoForm } from "./AreaAcopioSolidoForm";
import { AreaAcopioSolidoList } from "./AreaAcopioSolidoList";
import { crearAreaAcopioSolido } from "../utils/data";

export default function AcopioSolidosForm({ ...props }) {
  const { tramite } = useTramite();
  const { t } = useTranslation();
  const tramiteForm = useFormikContext();
  const unidadDimensionGranoCatalog = useCatalog(["unidad", "UM"], () =>
    buscarUnidad("UM"),
  );
  const unidadCantidadCatalog = useCatalog(["unidad", "TNANIO"], () =>
    buscarUnidad("TNANIO"),
  );
  const unidadSuperficieCatalog = useCatalog(["unidad", "M2"], () =>
    buscarUnidad("M2"),
  );
  const cercosPerimetralesCatalog = useCatalog(
    ["cercosPerimetrales"],
    () => listarCercosPerimetrales(null),
  );
  const loading =
    unidadDimensionGranoCatalog.loading ||
    unidadCantidadCatalog.loading ||
    unidadSuperficieCatalog.loading ||
    cercosPerimetralesCatalog.loading;
  const canLoadAcopioSolidos =
    tramiteForm.values?.conSolidosGranulares ||
    tramiteForm.values?.conSublimacionSolidos ||
    tramiteForm.values?.conAcopioSolidosGranel;
  const handleConSolidosGranularesChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleConSublimacionSolidosChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleConAcopioSolidosGranelChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleConApioSolidos1000m2Change = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleAreasAcopioSolidoChange = (event) => {
    tramiteForm.handleChange(event);
  };
  useEffect(() => {
    if (!canLoadAcopioSolidos) {
      tramiteForm.setFieldValue("areasAcopioSolidos", [], false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLoadAcopioSolidos]);
  return (
    <Loader loading={loading}>
      <Grid>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conSolidosGranulares"
              name="conSolidosGranulares"
              label={t("tramite.conSolidosGranulares")}
              value={tramiteForm.values?.conSolidosGranulares}
              onChange={handleConSolidosGranularesChange}
              isInvalid={isInvalid("conSolidosGranulares", tramiteForm)}
              error={t(tramiteForm.errors.conSolidosGranulares)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conSublimacionSolidos"
              name="conSublimacionSolidos"
              label={t("tramite.conSublimacionSolidos")}
              value={tramiteForm.values?.conSublimacionSolidos}
              onChange={handleConSublimacionSolidosChange}
              isInvalid={isInvalid("conSublimacionSolidos", tramiteForm)}
              error={t(tramiteForm.errors.conSublimacionSolidos)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conAcopioSolidosGranel"
              name="conAcopioSolidosGranel"
              label={t("tramite.conAcopioSolidosGranel")}
              value={tramiteForm.values?.conAcopioSolidosGranel}
              onChange={handleConAcopioSolidosGranelChange}
              isInvalid={isInvalid("conAcopioSolidosGranel", tramiteForm)}
              error={t(tramiteForm.errors.conAcopioSolidosGranel)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conAcopioSolidos1000m2"
              name="conAcopioSolidos1000m2"
              label={t("tramite.conAcopioSolidos1000m2")}
              value={tramiteForm.values?.conAcopioSolidos1000m2}
              onChange={handleConApioSolidos1000m2Change}
              isInvalid={isInvalid("conAcopioSolidos1000m2", tramiteForm)}
              error={t(tramiteForm.errors.conAcopioSolidos1000m2)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <ArrayField
              id="areasAcopioSolidos"
              name="areasAcopioSolidos"
              label={t("tramite.areasAcopioSolidos")}
              value={tramiteForm.values?.areasAcopioSolidos}
              onChange={handleAreasAcopioSolidoChange}
              createItem={crearAreaAcopioSolido}
              isInvalid={isInvalid("areasAcopioSolidos", tramiteForm)}
              error={
                (tramiteForm.errors.areasAcopioSolidos instanceof Array &&
                  t("errors.tramite.areasAcopioSolidos.many")) ||
                t(tramiteForm.errors.areasAcopioSolidos)
              }
              disabled={!isEditable(tramite) || !canLoadAcopioSolidos}
            >
              {({
                value: areasAcopioSolidos,
                append,
                remove,
                replace,
                select,
                selectedItem,
                selectedIndex,
                disabled,
              }) => (
                <Grid>
                  {!disabled && (
                    <Row>
                      <Col>
                        <AreaAcopioSolidoForm
                          value={selectedItem}
                          page={selectedIndex}
                          pages={areasAcopioSolidos.length}
                          select={select}
                          append={append}
                          replace={replace}
                          remove={remove}
                          unidadCantidad={unidadCantidadCatalog.data}
                          unidadDimensionGrano={
                            unidadDimensionGranoCatalog.data
                          }
                          cercosPerimetrales={cercosPerimetralesCatalog.data}
                          unidadSuperficie={unidadSuperficieCatalog.data}
                          disabled={disabled}
                        />
                      </Col>
                    </Row>
                  )}
                  <Row>
                    <Col>
                      <AreaAcopioSolidoList
                        value={areasAcopioSolidos}
                        select={select}
                        remove={remove}
                        disabled={disabled}
                      />
                    </Col>
                  </Row>
                </Grid>
              )}
            </ArrayField>
          </Col>
        </Row>
      </Grid>
    </Loader>
  );
}
